import { Box, Button, FormControl, InputAdornment, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from '@mui/material';
import { Store } from './../../../app/services/api/generated';
import CustomPagination from './../../../components/forms/Pagination';
import { ConfigurationLevel } from '../enums/ConfigurationLevel';
import { PodConfigurationModel } from './../../../app/services/api/podClient';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { mergeArrays } from '../helpers/ArrayHelper';
import { useTranslation } from 'react-i18next';
import { SearchSm } from './../../../assets';
import CustomSwitch from './../../../components/shared/CustomSwitch';

type IProps = {
    franchiseName: string,
    stores: Store[],
    existingStoreConfigurations: PodConfigurationModel[],
    canToggleEnabled: boolean,
    // eslint-disable-next-line no-unused-vars
    handleBatchSave: (configs: PodConfigurationModel[]) => Promise<void>;
}

const StoreConfigurationsList = (props: IProps) => {
  const { t } = useTranslation('managePodConfigurations');
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [search, setSearch] = useState<string>('');
  const [availableConfigs, setAvailableConfigs] = useState<PodConfigurationModel[]>([]);
  const [allConfigurations, setAllConfigurations] = useState<PodConfigurationModel[]>([]);
  const [initialConfigurations, setInitialConfigurations] = useState<PodConfigurationModel[]>([]);

  useEffect(() => {
    prepareConfigurations();
    setPage(0);
  }, [props.stores]);

  useEffect(() => {
    const configs = mergeArrays(availableConfigs, props.existingStoreConfigurations);
    const sortedConfigs = (configs ?? []).sort((a, b) => {
      return Number(b.enabled) - Number(a.enabled);
    });
    setAllConfigurations(sortedConfigs);
    setInitialConfigurations(sortedConfigs);
  }, [availableConfigs, props.existingStoreConfigurations]);

  const prepareConfigurations = () => {
    setAvailableConfigs(getAvailableConfigsFromStores());
  };

  const getAvailableConfigsFromStores = () => {
    return props.stores.map((x) => {
      const config: PodConfigurationModel = {
        id: 0,
        franchiseName: props.franchiseName ?? '',
        storeNumber: x.storeNumber ?? '',
        storeName: x.storeName ?? '',
        starbucksStoreNumber: x.starbucksStoreNumber ?? '',
        itemNumber: '',
        itemDescription: '',
        configurationLevel: ConfigurationLevel.Franchise,
        minStockOnHandQuantity: null,
        maxStockOnHandQuantity: null,
        maxOrderQuantity: null,
        safetyStock: null,
        isParEnabled: false,
        enabled: false,
        franchiseStores: [],
      };
      return config;
    });
  };

  /** Builds a unique key for a store-level POD configuration. */
  const getConfigKey = (config: PodConfigurationModel) =>
    `${config.franchiseName}-${config.storeNumber}`;

  /** Filters configurations by the current search query. */
  const filteredRows = () => {
    const lowerCaseQuery = search.toLowerCase();
    return lowerCaseQuery === '' ?
      allConfigurations :
      allConfigurations.filter(x => x.storeName.toLowerCase().includes(lowerCaseQuery) || x.storeNumber.toLowerCase().includes(lowerCaseQuery));
  };

  /** Returns the subset of filtered rows visible on the current page. */
  const visibleRows = () => {
    return filteredRows().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  /** Normalizes quantity fields for comparison, coercing numeric strings to numbers. */
  const normalizeForCompare = (config: PodConfigurationModel) => ({
    ...config,
    minStockOnHandQuantity:
      config.minStockOnHandQuantity === '' || config.minStockOnHandQuantity == null
        ? null
        : Number(config.minStockOnHandQuantity),
    maxStockOnHandQuantity:
      config.maxStockOnHandQuantity === '' || config.maxStockOnHandQuantity == null
        ? null
        : Number(config.maxStockOnHandQuantity),
    maxOrderQuantity:
      config.maxOrderQuantity === '' || config.maxOrderQuantity == null
        ? null
        : Number(config.maxOrderQuantity),
  });

  const changedConfigurations = useMemo((): PodConfigurationModel[] => {
    const initialMap = new Map(initialConfigurations.map(c => [getConfigKey(c), c]));
    return allConfigurations.filter(current => {
      const initial = initialMap.get(getConfigKey(current));
      if (!initial) return true;
      return JSON.stringify(normalizeForCompare(current)) !== JSON.stringify(normalizeForCompare(initial));
    });
  }, [allConfigurations, initialConfigurations]);

  const hasAnyPendingChanges = changedConfigurations.length > 0;

  /** Toggles the enabled state of a store configuration at the given visible index. */
  const handleToggle = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const { checked } = event.target;
    const row = visibleRows()[index];
    const rowKey = getConfigKey(row);
    const actualIndex = allConfigurations.findIndex(c => getConfigKey(c) === rowKey);
    if (actualIndex === -1) return;

    setAllConfigurations(prev => {
      const updated = [...prev];
      updated[actualIndex] = { ...updated[actualIndex], enabled: checked };
      return updated;
    });
  };

  /** Reverts all configurations to their initial state. */
  const handleCancelAll = () => {
    setAllConfigurations([...initialConfigurations]);
  };

  /** Saves all changed configurations via the parent batch-save handler. */
  const handleSaveAll = async () => {
    if (!changedConfigurations.length) return;
    try {
      await props.handleBatchSave(changedConfigurations);
      setInitialConfigurations([...allConfigurations]);
    } catch {
      // keep dirty state if save fails
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const maxPages = () => {
    return Math.ceil(filteredRows().length / rowsPerPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearch(newSearchValue);
    setPage(0);
  };

  /** Checks whether a configuration row has been modified from its initial state. */
  const isRowChanged = (config: PodConfigurationModel): boolean => {
    const initial = initialConfigurations.find(c => getConfigKey(c) === getConfigKey(config));
    if (!initial) return true;
    return JSON.stringify(normalizeForCompare(config)) !== JSON.stringify(normalizeForCompare(initial));
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <FormControl sx={{ flex: 1, maxWidth: 400 }}>
          <TextField placeholder={t('search')}
            size='small'
            autoComplete='off'
            id="store-search-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon><SearchSm /></SvgIcon>
                </InputAdornment>
              ),
            }}
            value={search}
            onChange={handleSearchChange}
            fullWidth
          />
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {hasAnyPendingChanges && (
            <Typography
              component="span"
              onClick={handleCancelAll}
              sx={{
                color: 'text.secondary',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {t('cancelAll')}
            </Typography>
          )}
          <Button
            variant="primary"
            size="md"
            onClick={handleSaveAll}
            disabled={!hasAnyPendingChanges}>
            {t('saveAll', { count: changedConfigurations.length })}
          </Button>
        </Box>
      </Box>
      <TableContainer sx={{ overflowY: 'auto' }}>
        <Table sx={{ tableLayout: 'fixed', minWidth: 700, overflow: 'hidden' }}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={3}>{t('storeName')}</TableCell>
              <TableCell>{t('storeNumber')}</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>{t('predictiveOrdering')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              visibleRows().map((row, index) => {
                const changed = isRowChanged(row);
                return (
                  <TableRow
                    key={getConfigKey(row)}
                    sx={changed ? {
                      height: '55px',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      '& > td:first-of-type': { borderLeft: `3px solid ${theme.palette.primary.main}` },
                    } : { height: '55px' }}
                  >
                    <TableCell colSpan={3}>
                      {row.storeName}
                    </TableCell>
                    <TableCell>
                      {row.storeNumber}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <CustomSwitch checked={row.enabled}
                        name='enabled'
                        disabled={!props.canToggleEnabled}
                        onChange={($event) => handleToggle($event, index)} />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <Box sx={{ minWidth: 700 }}>
          <CustomPagination
            page={page}
            setPage={handlePageChange}
            maxPages={maxPages()}
            breakpointForChangeDisplay={120000}
          ></CustomPagination>
        </Box>
      </TableContainer>
    </>
  );
};

export default StoreConfigurationsList;